import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { del, get, post, put } from 'common/HTTPClient'
import { DefaultHTTPResponse } from 'common/HTTPClient/DefaultHTTPResponse'
import { to } from 'common/helpers/awaitTo'
import {
  InstallationApplyRequest,
  InstallationDetail,
  InstallationErrorPayload,
  InstallationList,
  InstallationPlanResponse,
  InstallationRequestAccepted,
  InstallationRequestDetail,
} from 'common/types/kraken-core/Installation'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import { SearchInstallationsPageRequest } from './types'

function parseApplyError(error: unknown): InstallationErrorPayload {
  if (!error || typeof error !== 'object') {
    return { message: 'Failed to update installation' }
  }

  const axiosError = error as {
    response?: {
      status?: number
      data?: InstallationErrorPayload & { detail?: string }
    }
    message?: string
  }
  const status = axiosError.response?.status
  const data = axiosError.response?.data

  if (status === 409) {
    return {
      message:
        (typeof data?.detail === 'string' && data.detail) ||
        'An apply is already in progress for this installation. Try again shortly.',
    }
  }

  if (data && typeof data === 'object' && typeof data.message === 'string') {
    return {
      message: data.message,
      diagnostics: data.diagnostics,
      raw_output: data.raw_output,
    }
  }

  return { message: axiosError.message || 'Failed to update installation' }
}

export async function getInstallation(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<InstallationDetail>> {
  const [error, response] = await to(get(`/installations/${id}`, { ...config }))
  return { error, response }
}

export async function searchInstallations(
  data?: SearchInstallationsPageRequest,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<PaginatedResponse<InstallationList>>> {
  const params: SearchInstallationsPageRequest = {
    ...data,
    page: data?.page || 1,
    page_size: data?.page_size || 10,
  }
  const filterByName = data?.filters?.find(f => f.property === 'name').value
  if (filterByName) params.name = filterByName
  const filterByInstallationType = data?.filters?.find(f => f.property === 'installation_type')
    .value
  if (filterByInstallationType) params.installation_type = filterByInstallationType
  const [error, response] = await to(get('/installations', { ...config, params }))
  return { error, response }
}

export async function updateInstallation(
  installation: InstallationDetail,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<InstallationDetail>> {
  const [error, response] = await to(
    put(`/installations/${installation.id}`, installation, { ...config })
  )

  if (error) throw error

  return { error, response }
}

export async function deleteInstallation(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<InstallationDetail>> {
  const [error, response] = await to(del(`/installations/${id}`, { ...config }))

  if (error) throw error

  return { error, response }
}

export async function getInstallationsByPartnership(
  id: number,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<PaginatedResponse<InstallationList>>> {
  const [error, response] = await to(get(`/partnership/${id}/installations`, { ...config }))
  return { error, response }
}

export async function planInstallation(
  id: string,
  body: InstallationApplyRequest,
  config?: AxiosRequestConfig
): Promise<{
  error?: InstallationErrorPayload
  response?: AxiosResponse<InstallationPlanResponse>
}> {
  const [error, response] = await to(post(`/installations/${id}/plan`, body, { ...config }))

  if (error) {
    return { error: parseApplyError(error), response }
  }

  return { error, response }
}

export async function applyInstallation(
  id: string,
  body: InstallationApplyRequest,
  config?: AxiosRequestConfig
): Promise<{
  error?: InstallationErrorPayload
  response?: AxiosResponse<InstallationRequestAccepted>
}> {
  const [error, response] = await to(post(`/installations/${id}/apply`, body, { ...config }))

  if (error) {
    return { error: parseApplyError(error), response }
  }

  return { error, response }
}

export async function getInstallationRequest(
  requestId: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<InstallationRequestDetail>> {
  const [error, response] = await to(get(`/installation_requests/${requestId}`, { ...config }))
  return { error, response }
}
